/**
 * Desktop capabilities, with a browser fallback.
 *
 * Nova is a desktop application. It is also convenient to run the UI in a plain
 * browser during development, so every native capability is behind a check and
 * degrades to something usable rather than throwing.
 *
 * The folder picker is the clearest example of why the desktop shell earns its
 * keep: asking a curator to type a filesystem path is a browser limitation, not
 * a design decision. In the desktop app they get the OS dialog; in a browser tab
 * they get a text box, and the UI says which one they are looking at.
 */

export const isDesktop = (): boolean =>
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export interface EngineInfo {
  url: string;
  /** The shell started this engine, rather than attaching to one already running. */
  managed: boolean;
  /** Something is answering on the port right now. */
  running?: boolean;
  /** What was run, for the UI to show. */
  python?: string | null;
  /** Why the last start attempt failed, if it did. */
  lastError?: string | null;
  /** The engine's own last words, oldest first, when it would not start. */
  log?: string[];
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: tauriInvoke } = await import("@tauri-apps/api/core");
  return tauriInvoke<T>(command, args);
}

/**
 * Where the analysis engine is listening.
 *
 * In the desktop app the Rust side owns the engine's lifetime and reports the
 * port it settled on. In a browser we assume the documented default.
 */
/**
 * Where the engine is when Nova runs in a browser. When the engine itself
 * served this page (`python -m nova.api --web`), it marks the page, and the
 * engine is the page's own address -- which is what makes Nova usable from
 * another device. Otherwise (the Vite dev server) it is the local default.
 */
export function browserEngineUrl(): string {
  if (typeof document !== "undefined" && document.querySelector('meta[name="nova-engine"][content="same-origin"]')) {
    return window.location.origin;
  }
  return "http://127.0.0.1:8787";
}

/** True when Nova runs in a browser tab rather than the desktop app. */
export const isWeb = (): boolean => !isDesktop();

/**
 * Open another window of Nova (or bring it forward): the desktop app opens a
 * window of its own; a browser opens a tab or popup. `query` is added to the
 * page's address so the new window knows what it is for (`window=radio`).
 */
export async function openAppWindow(label: string, title: string, query: string, size = { width: 1440, height: 900 }): Promise<void> {
  if (isDesktop()) {
    await invoke("open_app_window", { label, title, query });
    return;
  }
  window.open(`${location.pathname}?${query}`, `nova-${label}`, `width=${size.width},height=${size.height}`);
}

/** Close a window opened with `openAppWindow` (in a browser, this window). */
export async function closeAppWindow(label: string): Promise<void> {
  if (isDesktop()) {
    await invoke("close_app_window", { label }).catch(() => window.close());
    return;
  }
  window.close();
}

export async function engineInfo(): Promise<EngineInfo> {
  if (!isDesktop()) {
    return { url: browserEngineUrl(), managed: false };
  }
  try {
    return await invoke<EngineInfo>("engine_info");
  } catch {
    return { url: "http://127.0.0.1:8787", managed: false };
  }
}

/** Native folder picker. Returns null when unavailable or cancelled. */
export async function pickFolder(): Promise<string | null> {
  if (!isDesktop()) return null;
  try {
    return await invoke<string | null>("pick_folder");
  } catch {
    return null;
  }
}

/** Show a file in Explorer / Finder. No-op in a browser. */
export async function revealInFileManager(path: string): Promise<void> {
  if (!isDesktop()) return;
  try {
    await invoke("reveal", { path });
  } catch {
    // Nothing useful to do; the user simply does not get the file manager.
  }
}

/**
 * Start the engine from inside the app.
 *
 * Used when the shell could not start it on launch. ``folder`` is where the
 * curator pointed Nova at the engine (the project folder, or the folder holding
 * a frozen engine); the shell remembers it for next time. Resolves when the
 * engine answers, or rejects with the reason it did not.
 */
export async function startEngine(folder?: string | null): Promise<EngineInfo> {
  if (!isDesktop()) throw new Error("Only the desktop app can start the engine.");
  return invoke<EngineInfo>("engine_start", { folder: folder ?? null });
}

/** Stop the engine this app started and start it again (after a code change). */
export async function restartEngine(): Promise<EngineInfo> {
  if (!isDesktop()) throw new Error("Only the desktop app can restart the engine.");
  return invoke<EngineInfo>("engine_restart");
}

/**
 * Save text the app built (an M3U playlist, a .novaflow) where the user chooses.
 *
 * Desktop: the native Save dialog, then the file is written. Browser: a normal
 * download. Returns where it went (desktop), "downloaded" (browser), or null
 * when the user cancelled.
 */
export async function saveTextFile(
  fileName: string,
  contents: string,
  filter: { name: string; extensions: string[] }
): Promise<string | null> {
  if (isDesktop()) {
    return invoke<string | null>("save_text_file", {
      fileName,
      contents,
      filterName: filter.name,
      extensions: filter.extensions,
    });
  }
  const url = URL.createObjectURL(new Blob([contents], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}

/**
 * Save bytes the app built (a PDF) where the user chooses: the native Save
 * dialog on the desktop, a normal download in a browser.
 */
export async function saveBinaryFile(
  fileName: string,
  contents: Uint8Array,
  filter: { name: string; extensions: string[]; mime: string }
): Promise<string | null> {
  if (isDesktop()) {
    return invoke<string | null>("save_binary_file", {
      fileName,
      contents: Array.from(contents),
      filterName: filter.name,
      extensions: filter.extensions,
    });
  }
  const url = URL.createObjectURL(new Blob([contents as Uint8Array<ArrayBuffer>], { type: filter.mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}

/** Ask for a file to open and return its text, or null when cancelled. */
export function openTextFile(accept: string): Promise<{ name: string; text: string } | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.style.display = "none";
    let settled = false;
    const done = (value: { name: string; text: string } | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return done(null);
      try {
        done({ name: file.name, text: await file.text() });
      } catch {
        done(null);
      }
    });
    input.addEventListener("cancel", () => done(null));
    document.body.appendChild(input);
    input.click();
  });
}
